import { useEffect, useMemo, useState } from 'react'
import { NavLink, useNavigate, useParams } from 'react-router-dom'
import {
  LayoutDashboard,
  Receipt,
  BarChart3,
  CalendarDays,
  Utensils,
  GlassWater,
  QrCode,
  Printer,
  Settings,
  LogOut,
  Store as StoreIcon,
  ExternalLink,
  CreditCard,
  Monitor,
  ClipboardList,
  Users,
  PiggyBank,
} from 'lucide-react'
import { useStoreOwner } from '../store/useStoreOwner'
import { useStaffAuth } from '../store/useStaffAuth'
import { useStoreRole } from '../hooks/useStoreRole'
import { canView, type RolePolicy } from '../lib/rolePolicy'
import { api } from '../services/api'
import { useStoreReservations } from '../hooks/useStoreReservations'
import { useI18n } from '../i18n/useI18n'
import { useLayoutPreference } from '../hooks/useLayoutPreference'

const STRIPE_DASHBOARD_URL = 'https://dashboard.stripe.com/'

type NavItem = {
  to: string
  labelKey: string
  icon: typeof LayoutDashboard
  badge?: number
  end?: boolean
}

/**
 * Vertical navigation rail for store-owner pages.
 *
 * Shown only at `lg:` and above; mobile / tablet keeps the existing in-page
 * navigation (back buttons + "Quick actions" grid on the dashboard).
 */
export default function StoreSidebar() {
  const navigate = useNavigate()
  const { storeId } = useParams<{ storeId: string }>()
  const { t } = useI18n()
  const { ownerName, companyName, ownedStores, storeLogout, stripeCapabilities } = useStoreOwner()
  const role = useStoreRole()
  const staffSession = useStaffAuth((s) => s.session)
  const clearStaff = useStaffAuth((s) => s.clearSession)
  const isStaff = role !== 'owner'
  const { pending } = useStoreReservations(storeId || null)
  const { preference } = useLayoutPreference()

  // #2199 店の情報はお店側の窓口から読む。以前は顧客側の一覧 (zustand の stores) を
  // 先に見ていたが、そちらはお客様に見せる項目だけになる (rolePolicy が無い)。
  // オーナーは自分の店一覧 (ownedStores) にあり、スタッフ (店長) は無いので取り直す。
  type SidebarStore = { id?: string; name?: string; reservationEnabled?: boolean; rolePolicy?: RolePolicy }
  const owned = storeId
    ? ownedStores.find((s) => (s as { id?: string }).id === storeId) as SidebarStore | undefined
    : undefined
  const [managed, setManaged] = useState<SidebarStore | null>(null)
  useEffect(() => {
    if (!storeId || owned?.id) return
    let cancelled = false
    api.getManagedStore(storeId)
      .then((data: SidebarStore) => { if (!cancelled) setManaged(data) })
      .catch((e: unknown) => console.error('[StoreSidebar] fetch store failed:', e))
    return () => { cancelled = true }
  }, [storeId, owned?.id])

  const currentStore = useMemo(() => {
    if (!storeId) return null
    const src = owned?.id ? owned : (managed?.id === storeId ? managed : null)
    if (!src?.id) return null
    return { id: src.id, name: src.name || '', reservationEnabled: src.reservationEnabled, rolePolicy: src.rolePolicy }
  }, [storeId, owned, managed])

  const items: NavItem[] = useMemo(() => {
    const dashItem: NavItem = {
      to: storeId ? `/store/dashboard/${storeId}` : '/store/dashboard',
      labelKey: 'storeNav.dashboard',
      icon: LayoutDashboard,
      end: true,
    }
    if (!storeId) return [dashItem]
    const orders: NavItem = { to: `/store/orders/${storeId}`, labelKey: 'storeNav.orders', icon: ClipboardList }
    const kitchen: NavItem = { to: `/store/kitchen/${storeId}`, labelKey: 'storeNav.kitchen', icon: Printer }
    const reservations: NavItem = { to: `/store/reservations/${storeId}`, labelKey: 'storeNav.reservations', icon: CalendarDays, badge: pending.length }
    const courses: NavItem = { to: `/store/courses/${storeId}`, labelKey: 'storeNav.courses', icon: Utensils }
    const transactions: NavItem = { to: `/store/transactions/${storeId}`, labelKey: 'storeNav.transactions', icon: Receipt }
    const report: NavItem = { to: `/store/report/${storeId}`, labelKey: 'storeNav.report', icon: BarChart3 }

    // #1439 オーナーが決めた「見せるもの」。予約が出せるのは、その店で予約機能自体が
    // ON のときだけ（rolePolicy はあくまで見せる/見せないの上書き）。
    const policy = currentStore?.rolePolicy
    const showReservations = !!currentStore?.reservationEnabled && canView(role, 'reservations', policy)
    const showSales = canView(role, 'sales', policy)
    const showTransactions = canView(role, 'transactions', policy)

    // #995/#1439 バイト: 店内注文 / 厨房 が基本。予約・売上・取引履歴はオーナーが
    // 見せると決めたぶんだけ増える（閲覧のみ。承認やキャンセルは画面側で出さない）。
    if (role === 'staff') {
      const list: NavItem[] = [orders, kitchen]
      if (showReservations) list.push(reservations)
      if (showSales) list.push(report)
      if (showTransactions) list.push(transactions)
      return list
    }

    // #1353 放題プランは**予約とは独立**。ウォークインの卓にも付けるので、
    // courses と違い reservationEnabled でゲートしない。
    const allYouCan: NavItem = { to: `/store/all-you-can/${storeId}`, labelKey: 'storeNav.allYouCan', icon: GlassWater }
    const staffMgmt: NavItem = { to: `/store/staff/${storeId}`, labelKey: 'storeNav.staff', icon: Users }

    // #995/#1440 店長: 実務＋スタッフ管理＋**店舗設定**。設定は backend
    // (PUT /stores/{id} = owner|manager) が元々許しているもので、扱うのは店名・営業時間・
    // 予約/出前設定などの運営設定だけ。口座変更・Connect・KYC・退会は別エンドポイントで
    // オーナー専用のまま。以前はナビから外していたが、URL 直打ちとの差が残るだけだった。
    // 売上・取引履歴・予約は #1439 の rolePolicy に従う（既定は全て可）。
    if (role === 'manager') {
      const list: NavItem[] = [orders, kitchen]
      if (showReservations) list.push(reservations, courses)
      list.push(allYouCan)
      if (showSales) list.push(report)
      if (showTransactions) list.push(transactions)
      list.push(staffMgmt, { to: `/store/settings/${storeId}`, labelKey: 'storeNav.settings', icon: Settings })
      return list
    }

    // オーナー: 全部（店内注文・スタッフ管理を含む）。
    // #2194 利益の目安はオーナーだけ (人件費・家賃をスタッフに見せない)
    const profit: NavItem = { to: `/store/profit/${storeId}`, labelKey: 'storeNav.profit', icon: PiggyBank }
    const list: NavItem[] = [dashItem, orders, transactions, report, profit]
    if (currentStore?.reservationEnabled) list.push(reservations, courses)
    list.push(
      allYouCan,
      { to: `/store/qr/${storeId}`, labelKey: 'storeNav.qr', icon: QrCode },
      kitchen,
      staffMgmt,
      { to: `/store/settings/${storeId}`, labelKey: 'storeNav.settings', icon: Settings },
    )
    return list
  }, [storeId, role, currentStore?.reservationEnabled, currentStore?.rolePolicy, pending.length])

  const handleLogout = async () => {
    // #995 スタッフはセッションをクリアして staff ログインへ。
    if (isStaff) {
      clearStaff()
      navigate('/staff/login')
      return
    }
    await storeLogout()
    navigate('/store/login')
  }

  const stripeConnected = !isStaff && stripeCapabilities?.chargesEnabled === true

  // Only render the SaaS sidebar for the experiment-1 layout (owner).
  // #995 スタッフ/店長は下部タブバー(StaffTabBar)がナビ。サイドバーは owner 専用。
  if (isStaff || preference !== 'experiment-1') return null

  return (
    <nav
      aria-label="Store navigation"
      className="hidden lg:flex lg:sticky lg:top-0 lg:h-screen lg:w-60 xl:w-64 flex-col border-r border-gray-100 bg-white"
    >
      {/* Brand — clickable, returns to the multi-store list (/store/dashboard). */}
      <button
        type="button"
        onClick={() => navigate(isStaff && storeId ? `/store/orders/${storeId}` : '/store/dashboard')}
        className="flex items-center gap-2.5 px-5 py-5 border-b border-gray-100 w-full text-left tap-effect hover:bg-gray-50/60 transition-colors"
      >
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-yokoso-red text-white">
          <StoreIcon size={18} />
        </div>
        <div className="min-w-0">
          <p className="font-display text-sm font-bold text-text-primary leading-tight">YOKOSO PAY</p>
          <p className="font-body text-[10px] uppercase tracking-wider text-text-tertiary">
            {t('storeNav.portal')}
          </p>
        </div>
      </button>

      {/* Current store context — clickable for quick switching back to list. */}
      {currentStore && (
        <button
          type="button"
          onClick={() => navigate(isStaff && storeId ? `/store/orders/${storeId}` : '/store/dashboard')}
          className="px-5 py-3 border-b border-gray-100 w-full text-left tap-effect hover:bg-gray-50/60 transition-colors group"
        >
          <p className="font-body text-[10px] uppercase tracking-wider text-text-tertiary">
            {t('storeNav.currentStore')}
          </p>
          <p className="mt-1 font-jp font-semibold text-sm text-text-primary truncate group-hover:text-yokoso-red transition-colors">
            {currentStore.name}
          </p>
          <p className="mt-1 font-body text-[10px] text-text-tertiary">
            {t('storeNav.backToList')}
          </p>
        </button>
      )}

      {/* Nav */}
      <div className="flex-1 overflow-y-auto px-3 py-4">
        <ul className="space-y-0.5">
          {items.map(item => {
            const Icon = item.icon
            return (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `group relative flex items-center gap-3 rounded-lg px-3 py-2.5 font-body text-sm transition-colors ${
                      isActive
                        ? 'bg-yokoso-red/10 text-yokoso-red font-semibold'
                        : 'text-text-secondary hover:bg-gray-50 hover:text-text-primary'
                    }`
                  }
                >
                  <Icon size={18} className="shrink-0" />
                  <span className="flex-1 truncate">{t(item.labelKey)}</span>
                  {item.badge !== undefined && item.badge > 0 && (
                    <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-yokoso-red px-1.5 text-[10px] font-bold text-white">
                      {item.badge}
                    </span>
                  )}
                </NavLink>
              </li>
            )
          })}
        </ul>

        {/* Stripe shortcut (only when approved) */}
        {stripeConnected && (
          <div className="mt-6 rounded-lg border border-gray-100 bg-gray-50/60 p-3">
            <div className="flex items-center gap-2">
              <CreditCard size={14} className="text-matcha" />
              <p className="font-body text-xs font-semibold text-text-primary">Stripe</p>
            </div>
            <p className="mt-1 font-body text-[11px] text-text-tertiary leading-relaxed">
              {t('storeNav.stripeDesc')}
            </p>
            <a
              href={STRIPE_DASHBOARD_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-1 font-body text-[11px] font-semibold text-navy hover:text-yokoso-red transition-colors"
            >
              {t('storeNav.openStripe')} <ExternalLink size={10} />
            </a>
          </div>
        )}
      </div>

      {/* Footer: account + personal preferences + logout.
       * 表示設定 is a per-owner preference (not per-store), so it lives in the
       * account footer rather than the store-management nav above. */}
      <div className="shrink-0 border-t border-gray-100 px-5 py-4">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-navy/10 font-display text-xs font-bold text-navy">
            {(isStaff ? (staffSession?.name || '?') : (ownerName || companyName || '?')).slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-body text-xs font-semibold text-text-primary truncate">
              {isStaff ? staffSession?.name : (companyName || ownerName || t('storeNav.owner'))}
            </p>
            {isStaff ? (
              <p className="font-body text-[11px] text-text-tertiary truncate">
                {staffSession?.role === 'manager' ? t('storeStaff.roleManager') : t('storeStaff.roleStaff')}
              </p>
            ) : (companyName && ownerName && (
              <p className="font-body text-[11px] text-text-tertiary truncate">{ownerName}</p>
            ))}
          </div>
        </div>
        {!isStaff && (
          <button
            onClick={() => navigate('/store/preferences')}
            className="tap-effect mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg border border-gray-100 py-2 font-body text-xs font-medium text-text-secondary hover:bg-gray-50 hover:text-text-primary transition-colors"
          >
            <Monitor size={12} />
            {t('storeNav.preferences')}
          </button>
        )}
        <button
          onClick={handleLogout}
          className="tap-effect mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-gray-100 py-2 font-body text-xs font-medium text-text-secondary hover:bg-gray-50 hover:text-red-500 transition-colors"
        >
          <LogOut size={12} />
          {t('storeAuth.logout')}
        </button>
      </div>
    </nav>
  )
}
